import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, type FileRef, type User } from "../api";
import { matchEmoji } from "../emoji";
import { sendTyping } from "../live";
import { useStore } from "../store";
import { Avatar, sizeLabel, toast } from "./ui";
import { Icon } from "./icons";
import { QUICK } from "./Reactions";

interface Upload { key: string; name: string; size: number; progress: number; ref?: FileRef; failed?: boolean }

export interface ComposerHandle { focus: () => void; addFiles: (f: File[]) => void }

interface Props {
  placeholder: string;
  draftKey: string;
  onSend: (body: string, files: FileRef[], alsoInChat: boolean) => boolean | void | Promise<boolean | void>;
  files?: boolean;
  alsoOption?: string;
  preview?: (text: string) => ReactNode;
  autoFocus?: boolean;
  /** The chat (and thread) this box writes into: for @people, "typing…" and scheduling. */
  roomId?: number;
  parentId?: number | null;
  /** ↑ in an empty box. */
  onEditLast?: () => void;
  onSchedule?: (body: string, files: FileRef[], alsoInChat: boolean, sendAt: Date) => Promise<boolean | void>;
}

const readDraft = (k: string) => { try { return localStorage.getItem(`draft:${k}`) ?? ""; } catch { return ""; } };
const writeDraft = (k: string, v: string) => { try { v ? localStorage.setItem(`draft:${k}`, v) : localStorage.removeItem(`draft:${k}`); } catch {} };

const MORE_EMOJI = ["😂", "🎉", "❤️", "🔥", "👏", "💯", "🙌", "😅", "😊", "🤔", "😮", "😢", "👌", "💪", "⚡", "🚀", "📌", "⏳", "❌", "⚠️", "💰", "📞", "📎", "🏨", "☕", "🙈", "👋", "🤝"];

type Suggest = { kind: "people"; items: User[]; start: number } | { kind: "emoji"; items: [string, string][]; start: number };

function nextAt(day: number | "tomorrow", hour = 9) {
  const d = new Date();
  if (day === "tomorrow") d.setDate(d.getDate() + 1);
  else { const add = ((day - d.getDay() + 7) % 7) || 7; d.setDate(d.getDate() + add); }
  d.setHours(hour, 0, 0, 0);
  return d;
}

export const Composer = forwardRef<ComposerHandle, Props>(function Composer(props, ref) {
  const { placeholder, draftKey, onSend, files = true, alsoOption, preview, autoFocus, roomId, parentId = null, onEditLast, onSchedule } = props;
  const [text, setText] = useState(() => readDraft(draftKey));
  const [ups, setUps] = useState<Upload[]>([]);
  const [also, setAlso] = useState(false);
  const [fmt, setFmt] = useState(() => { try { return localStorage.getItem("hub:fmt") === "1"; } catch { return false; } });
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const [sug, setSug] = useState<Suggest | null>(null);
  const [sel, setSel] = useState(0);
  const ta = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const me = useStore((s) => s.me);
  const users = useStore((s) => s.users);
  const room = useStore((s) => (roomId ? s.rooms[roomId] : undefined));
  const people = useMemo(() => (room ? room.members.filter((u) => u !== me?.id).map((u) => users[u]).filter((u) => u?.active) : []), [room, users, me?.id]);

  useEffect(() => { setText(readDraft(draftKey)); setUps([]); setAlso(false); setSug(null); }, [draftKey]);
  useEffect(() => {
    const t = ta.current;
    if (autoFocus && t) { t.focus(); t.selectionStart = t.selectionEnd = t.value.length; }
  }, [draftKey, autoFocus]);
  useLayoutEffect(() => { const t = ta.current; if (t) { t.style.height = "auto"; t.style.height = Math.min(t.scrollHeight, 240) + "px"; } }, [text, fmt]);

  const addFiles = (list: File[]) => {
    if (!files) return;
    for (const f of list) {
      const key = crypto.randomUUID();
      setUps((u) => [...u, { key, name: f.name, size: f.size, progress: 0 }]);
      api.upload(f, (p) => setUps((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))))
        .then((r) => setUps((u) => u.map((x) => (x.key === key ? { ...x, ref: r, progress: 1 } : x))))
        .catch((e) => { toast(e.message || "Upload failed"); setUps((u) => u.map((x) => (x.key === key ? { ...x, failed: true } : x))); });
    }
    ta.current?.focus();
  };
  useImperativeHandle(ref, () => ({ focus: () => ta.current?.focus(), addFiles }));

  const change = (v: string, caret = v.length) => {
    setText(v); writeDraft(draftKey, v);
    if (roomId && v.trim()) sendTyping(roomId, parentId);
    // What's being typed right before the caret: "@han" or ":thu".
    const before = v.slice(0, caret);
    const at = before.match(/(?:^|\s)@([\p{L}\p{N}_]*)$/u);
    const colon = before.match(/(?:^|\s):([a-z0-9_+]{2,})$/i);
    if (at && people.length) {
      const items = people.filter((u) => u.name.toLowerCase().startsWith(at[1].toLowerCase())).slice(0, 6);
      setSug(items.length ? { kind: "people", items, start: caret - at[1].length - 1 } : null);
    } else if (colon) {
      const items = matchEmoji(colon[1]);
      setSug(items.length ? { kind: "emoji", items, start: caret - colon[1].length - 1 } : null);
    } else setSug(null);
    setSel(0);
  };

  const replaceRange = (start: number, end: number, insert: string) => {
    const v = text.slice(0, start) + insert + text.slice(end);
    change(v, start + insert.length);
    setSug(null);
    requestAnimationFrame(() => { const t = ta.current; if (t) { t.focus(); t.selectionStart = t.selectionEnd = start + insert.length; } });
  };
  const choose = (i: number) => {
    if (!sug) return;
    const caret = ta.current?.selectionStart ?? text.length;
    if (sug.kind === "people") replaceRange(sug.start, caret, `@${sug.items[i].name} `);
    else replaceRange(sug.start, caret, `${sug.items[i][1]} `);
  };
  const insert = (s: string) => {
    const t = ta.current!; const a = t.selectionStart ?? text.length, b = t.selectionEnd ?? a;
    replaceRange(a, b, s);
  };
  /** Wraps the selection (or the word being typed) in Slack's markers. */
  const wrap = (l: string, r = l, block = false) => {
    const t = ta.current!; const a = t.selectionStart, b = t.selectionEnd;
    const inner = text.slice(a, b) || (block ? "" : "");
    const v = text.slice(0, a) + l + inner + r + text.slice(b);
    change(v, a + l.length + inner.length);
    requestAnimationFrame(() => { t.focus(); t.selectionStart = a + l.length; t.selectionEnd = a + l.length + inner.length; });
  };
  const prefixLines = (p: (i: number) => string) => {
    const t = ta.current!; const a = text.lastIndexOf("\n", t.selectionStart - 1) + 1, b = t.selectionEnd;
    const block = text.slice(a, b).split("\n").map((l, i) => p(i) + l).join("\n");
    change(text.slice(0, a) + block + text.slice(b), a + block.length);
    requestAnimationFrame(() => t.focus());
  };
  // A small box in the formatting bar instead of a browser prompt (the desktop app has none).
  const [linking, setLinking] = useState<{ a: number; b: number } | null>(null);
  const [url, setUrl] = useState("");
  const link = () => { const t = ta.current!; setLinking({ a: t.selectionStart, b: t.selectionEnd }); setUrl(""); };
  const putLink = () => {
    if (!linking || !/^https?:\/\/\S+$/i.test(url.trim())) return setLinking(null);
    const label = text.slice(linking.a, linking.b);
    replaceRange(linking.a, linking.b, label ? `${label} (${url.trim()})` : url.trim());
    setLinking(null);
  };

  const busy = ups.some((u) => !u.ref && !u.failed);
  const ready = !busy && (text.trim().length > 0 || ups.some((u) => u.ref));
  const clear = () => { setText(""); writeDraft(draftKey, ""); setUps([]); setAlso(false); setSug(null); };
  const submit = async () => {
    const body = text.trim();
    const done = ups.filter((u) => u.ref).map((u) => u.ref!);
    if ((!body && !done.length) || busy) return;
    const ok = await onSend(body, done, also);
    if (ok === false) return;
    clear();
  };
  const scheduleAt = async (at: Date) => {
    setSchedOpen(false);
    if (!onSchedule || !ready) return;
    const ok = await onSchedule(text.trim(), ups.filter((u) => u.ref).map((u) => u.ref!), also, at);
    if (ok === false) return;
    clear();
    toast(`Scheduled for ${at.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`);
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (sug) {
      if (e.key === "ArrowDown") { e.preventDefault(); return setSel((s) => (s + 1) % sug.items.length); }
      if (e.key === "ArrowUp") { e.preventDefault(); return setSel((s) => (s - 1 + sug.items.length) % sug.items.length); }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); return choose(sel); }
      if (e.key === "Escape") { e.preventDefault(); return setSug(null); }
    }
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === "b") { e.preventDefault(); return wrap("*"); }
    if (mod && e.key.toLowerCase() === "i") { e.preventDefault(); return wrap("_"); }
    if (mod && e.shiftKey && e.key.toLowerCase() === "x") { e.preventDefault(); return wrap("~"); }
    if (e.key === "ArrowUp" && !text && onEditLast) { e.preventDefault(); return onEditLast(); }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); }
  };

  const toggleFmt = () => { setFmt((f) => { try { localStorage.setItem("hub:fmt", f ? "0" : "1"); } catch {} return !f; }); ta.current?.focus(); };

  return (
    <div className="comp">
      {preview && text.trim() && <div className="preview">{preview(text)}</div>}
      {sug && (
        <div className="sug">
          {sug.kind === "people"
            ? sug.items.map((u, i) => <button key={u.id} className={i === sel ? "on" : ""} onMouseEnter={() => setSel(i)} onMouseDown={(e) => { e.preventDefault(); choose(i); }}><Avatar user={u} size={20} dot />{u.name}{u.status && <small>{u.status}</small>}</button>)
            : sug.items.map(([c, em], i) => <button key={c} className={i === sel ? "on" : ""} onMouseEnter={() => setSel(i)} onMouseDown={(e) => { e.preventDefault(); choose(i); }}><span className="em">{em}</span>:{c}:</button>)}
        </div>
      )}
      <div className="box slack">
        {ups.length > 0 && (
          <div className="att">
            {ups.map((u) => (
              <span key={u.key}>{u.name} <i>{u.failed ? "failed" : u.ref ? sizeLabel(u.size) : `${Math.round(u.progress * 100)}%`}</i>
                <button onClick={() => setUps((x) => x.filter((y) => y.key !== u.key))}>×</button></span>
            ))}
          </div>
        )}
        <textarea ref={ta} rows={1} value={text} placeholder={placeholder} spellCheck
          onChange={(e) => change(e.target.value, e.target.selectionStart)}
          onPaste={(e) => { const f = [...e.clipboardData.files]; if (f.length && files) { e.preventDefault(); addFiles(f); } }}
          onKeyDown={onKey} onBlur={() => setTimeout(() => setSug(null), 150)} />
        <div className="bar">
          {files && <><button className="fb" title="Add a file" onClick={() => picker.current?.click()}><Icon n="plus" /></button><span className="sep" /></>}
          <button className={`fb${fmt ? " on" : ""}`} title="Formatting" onClick={toggleFmt}><Icon n="aa" /></button>
          <span className="emw">
            <button className="fb" title="Emoji" onClick={() => setEmojiOpen((o) => !o)}><Icon n="smile" /></button>
            {emojiOpen && (
              <div className="picker up" onMouseLeave={() => setEmojiOpen(false)}>
                {[...QUICK, ...MORE_EMOJI].map((e) => <button key={e} onClick={() => { insert(e); setEmojiOpen(false); }}>{e}</button>)}
              </div>
            )}
          </span>
          {roomId && <button className="fb" title="Mention someone" onClick={() => insert(text && !/\s$/.test(text.slice(0, ta.current?.selectionStart ?? 0)) ? " @" : "@")}><Icon n="at" /></button>}
          {fmt && (
            <>
              <span className="sep" />
              <button className="fb" title="Bold (⌘B)" onClick={() => wrap("*")}><Icon n="bold" /></button>
              <button className="fb" title="Italic (⌘I)" onClick={() => wrap("_")}><Icon n="italic" /></button>
              <button className="fb" title="Strikethrough (⌘⇧X)" onClick={() => wrap("~")}><Icon n="strike" /></button>
              <button className="fb" title="Link" onClick={link}><Icon n="link" /></button>
              <button className="fb" title="Bulleted list" onClick={() => prefixLines(() => "- ")}><Icon n="list" /></button>
              <button className="fb" title="Numbered list" onClick={() => prefixLines((i) => `${i + 1}. `)}><Icon n="olist" /></button>
              <button className="fb" title="Quote" onClick={() => prefixLines(() => "> ")}><Icon n="quote" /></button>
              <button className="fb" title="Code" onClick={() => wrap("`")}><Icon n="code" /></button>
              <button className="fb" title="Code block" onClick={() => wrap("```\n", "\n```", true)}><Icon n="codeblock" /></button>
              {linking && <input className="lk" autoFocus placeholder="Paste the link, then Enter" value={url} onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); putLink(); } if (e.key === "Escape") setLinking(null); }} onBlur={() => setLinking(null)} />}
            </>
          )}
          <span className="sendw">
            <button className={`send${ready ? " on" : ""}`} title="Send (Enter)" disabled={!ready} onClick={submit}><Icon n="send" /></button>
            {onSchedule && <button className={`chev${ready ? " on" : ""}`} title="Schedule for later" disabled={!ready} onClick={() => setSchedOpen((o) => !o)}><Icon n="chev" s={12} /></button>}
            {schedOpen && (
              <div className="menu up">
                <h6>Schedule message</h6>
                <button className="mi" onClick={() => scheduleAt(nextAt("tomorrow"))}><Icon n="clock" /><span>Tomorrow at 9:00</span></button>
                <button className="mi" onClick={() => scheduleAt(nextAt(1))}><Icon n="clock" /><span>Monday at 9:00</span></button>
                <div className="custom">
                  <input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} />
                  <button className="btn" disabled={!custom} onClick={() => scheduleAt(new Date(custom))}>Schedule</button>
                </div>
              </div>
            )}
          </span>
          <input ref={picker} type="file" multiple hidden onChange={(e) => { addFiles([...(e.target.files ?? [])]); e.target.value = ""; }} />
        </div>
        {alsoOption && <label className="opt"><input type="checkbox" checked={also} onChange={(e) => setAlso(e.target.checked)} />{alsoOption}</label>}
      </div>
    </div>
  );
});
